import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const PREF_FIELD = {
  welcome: 'welcomeView',
  filters: 'filtersView',
  adminCustomers: 'adminCustomersView',
};

const LEGACY_STORAGE_KEY = {
  welcome: 'welcome-view-mode',
  filters: 'filters-view-mode',
  adminCustomers: 'admin-customers-view-mode',
};

function readLegacyLocal(page) {
  try {
    const v = localStorage.getItem(LEGACY_STORAGE_KEY[page]);
    return v === 'list' || v === 'cards' ? v : null;
  } catch {
    return null;
  }
}

function clearLegacyLocal(page) {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY[page]);
  } catch {
    /* ignore */
  }
}

/**
 * Cards/list view mode persisted per signed-in user (server-side).
 */
export function useViewMode(page) {
  const { authenticated, loading, uiPreferences, updateUiPreferences } = useAuth();
  const field = PREF_FIELD[page];
  const serverMode = uiPreferences?.[field];

  const [viewMode, setViewModeLocal] = useState('cards');
  const [migratedLegacy, setMigratedLegacy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!authenticated) {
      setViewModeLocal('cards');
      setMigratedLegacy(false);
      return;
    }
    const mode = serverMode === 'list' || serverMode === 'cards' ? serverMode : 'cards';
    setViewModeLocal(mode);

    if (!migratedLegacy && serverMode !== 'list' && serverMode !== 'cards') {
      const legacy = readLegacyLocal(page);
      if (legacy) {
        clearLegacyLocal(page);
        setMigratedLegacy(true);
        updateUiPreferences({ [field]: legacy });
        setViewModeLocal(legacy);
      }
    }
  }, [loading, authenticated, serverMode, page, field, migratedLegacy, updateUiPreferences]);

  const setViewMode = useCallback((mode) => {
    if (mode !== 'cards' && mode !== 'list') return;
    setViewModeLocal(mode);
    if (authenticated) {
      updateUiPreferences({ [field]: mode });
    }
  }, [authenticated, field, updateUiPreferences]);

  return [viewMode, setViewMode];
}
